import type { JSX, PropsWithChildren } from 'react';
import { useCallback } from 'react';
import { styled } from 'styled-components';
import { Root as RadioGroupRoot } from '@radix-ui/react-radio-group';
import { useColorPickerState } from './ColorPickerContext';
import { convertToHsv } from './color-conversion-utils';
import { Label } from '../Label';

const Container = styled.div`
  display: grid;
  gap: var(--wui-space-01);
  grid-template-columns: minmax(0, 1fr);
`;

const LabelContainer = styled.div`
  padding-top: var(--wui-space-01);
`;

const ItemsContainer = styled(RadioGroupRoot)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: start;
  padding: var(--wui-space-01) 6px;
  gap: var(--wui-space-02);
`;

export type ColorGridProps = PropsWithChildren & {
  label?: string;
};

/**
 * Displays color options as a wrapped grid of swatches. Use `ColorGridOption`
 * as children and pass an optional `label` for a heading.
 */
export const ColorGrid = ({ children, label }: ColorGridProps): JSX.Element => {
  const { valueAsHex, onChangeNonDerivedValueAsHsv } = useColorPickerState();

  const onValueChange = useCallback(
    (value: string) => {
      const hsv = convertToHsv(value);
      onChangeNonDerivedValueAsHsv(hsv);
    },
    [onChangeNonDerivedValueAsHsv],
  );

  return (
    <Container>
      {label != null && (
        <LabelContainer>
          <Label>{label}</Label>
        </LabelContainer>
      )}

      <ItemsContainer
        onValueChange={onValueChange}
        value={valueAsHex}
      >
        {children}
      </ItemsContainer>
    </Container>
  );
};

ColorGrid.displayName = 'ColorGrid';
